import logger from "js/core/logger";
import {
    AuthoringElementType,
    AuthoringBlockType,
    TextStyleType,
    AuthoringShapeType,
    VerticalAlignType,
    AssetType,
    ParagraphStyle
} from "common/constants";
import { DecorationType } from "js/elements/base/DecorationElement";
import { TextElement } from "js/elements/base/TextElement";
import { ContentElement } from "js/elements/base/ContentElement";
import { SVGElement } from "js/elements/base/SVGElement";
import ConnectorItem from "js/elements/elements/connectors/ConnectorItem";
import { _, tinycolor } from "js/vendor";
import { app } from "js/namespaces";
import * as geom from "js/core/utilities/geom";
import { isObjectDiff, trackActivity } from "js/core/utilities/utilities";
import { v4 as uuid } from "uuid";
import { StreetMap } from "js/elements/elements/infographics/StreetMap";
import { Video } from "js/elements/elements/Video";
import { isArray, isNumber } from "lodash-es";
import { Icon } from "js/elements/base/MediaElements/IconElement";
import { FramedMediaElement } from "js/elements/base/MediaElements/FramedMediaElement";

const log = logger.info(logger.CONVERT_TO_AUTHORING).bind(logger);

export async function ConvertSlideToAuthoring(canvas) {
    if (canvas.model.template_id === "authoring") {
        return;
    }

    if (canvas.showSmartSlideWatermark()) {
        return;
    }

    const converted = [];
    await convertElementToAuthoring(canvas.layouter.canvasElement, converted);

    if (converted.length == 0) {
        log("Nothing to convert");
        return;
    }

    let userFontScale = {};
    for (let convertedModel of converted) {
        // move any userFontScale from converted elements
        if (convertedModel.userFontScale) {
            userFontScale = { ...convertedModel.userFontScale };
            delete convertedModel.userFontScale;
        }
    }

    trackActivity("Slide", "ConvertedToClassic", null, null, { slide_id: canvas.dataModel.id, template: canvas.model.template_id }, { audit: true });

    // check for background conversions
    let { backgroundColor, backgroundStyle } = canvas.model.layout;
    if (backgroundColor && !!backgroundColor.toRgbString) {
        backgroundColor = backgroundColor?.toRgbString();
    }

    canvas.model = {
        template_id: "authoring",
        elements: {
            primary: {
                elements: converted,
                userFontScale
            }
        },
        layout: {
            headerPosition: "none",
            showFooter: "off",
            slideColor: canvas.model.layout.slideColor,
            trayLayout: "none",
            elementTextBlockPosition: "none",
            backgroundColor,
            backgroundStyle
        },
        animations: {}
    };

    canvas.updateTemplate("authoring");
    canvas.updateCanvasModel().then(() => {
        app.mainView.editorView.updateCurrentCanvasUI();
    });
}

async function exportCustomAsGroup(element, converted) {
    const groupElement = target => exportSelfAsGroup(target, converted);
    const groupChildren = target => exportChildrenAsGroup(target, converted);
    const exportAll = async (target, allowGrouping = true) => {
        target.isExporting = true;
        const children = [];
        await convertElementToAuthoring(target, children, allowGrouping);
        converted.push(...children);
        return children;
    };
    await element.convertToAuthoring(groupElement, groupChildren, exportAll);
}

async function exportSelfAsGroup(element, converted, allowGrouping = false) {
    const convert = isArray(element) ? { elements: element } : element;
    const children = [];
    await convertElementToAuthoring(convert, children, allowGrouping);
    groupAll(children, converted);
}

async function exportChildrenAsGroup(element, converted, allowGrouping = false) {
    const convert = isArray(element) ? { elements: element } : element;
    const children = [];
    await exportElementChildren(convert, children, allowGrouping);
    groupAll(children, converted);
}

function groupAll(elements, appendTo) {
    const groupId = uuid();
    for (const element of elements) {
        element.groupIds = [groupId];
        appendTo?.push(element);
    }
}

function isInsideElementType(element, type) {
    let check = element;
    while (check.parentElement) {
        if (check.parentElement.type === type) {
            return true;
        }

        check = check.parentElement;
    }

    return false;
}

async function convertElementToAuthoring(element, converted, allowGrouping = true) {
    if (element.options.preventExport) return;
    if (!element.DOMNode || element.DOMNode.style.display == "none") return;

    // console.log(`converting ${element.type}, path: ${element.uniquePath}, id: ${element.id}`);

    // allow for grouping
    if (allowGrouping) {
        switch (element.type) {
            case "AgendaItem":
            case "AgendaSection":
            case "RadialBarChartItem":
            case "LogoGridItem":
            case "TextListItem":
            case "GanttChartTask":
            case "TextBoxGridItem":
            case "StackDiagramItem":
            case "UserTestimonialItem":
            case "ProcessDiagramBoxItem":
            case "SwotDiagramItem":
            case "BoxOrgChartNode":
            case "TimelineItem":
            case "TimelineMarker":
            case "ContactItemIcon":
            case "CircleOrgChartNode":
            case "BigNumberItem":
            case "TextInShapeContentItem":
            case "NumberedTextContentItem":
            case "MonthCalendar":
            case "PictureOrgChartNode":
            case "HorizontalTaskElement":
            case "ProductScreenshotTextListItem":
            case "JourneyStart":
            case "JourneyEnd":
            case "MinimalOrgChartNode":
            case "GridComponent":
            case "CompareRadialBarItem":
            case "PresentationLogoItem":
                return exportSelfAsGroup(element, converted);

            case "BulletTextContentItem":
            case "FramedMediaElement":
            case "TextContentItem":
            case "TargetBandSegment":
            case "SliceChartItem":
            case "MediaAndTextContentItem":
            case "VerticalTaskElement":
            case "TeamItem":
            case "CompareVerticalBarItem":
            case "ProcessDiagramChevronItem":
            case "ArrowBarItem":
            case "PhotoTextListItem":
            case "IconTextGridItem":
            case "CompareBubbleItem":
            case "CompareHorizontalBarItem":
            case "CompareVerticalPictographItem":
            case "VennDiagramItem":
            case "FlexCircleContentItem":
            case "PictorialChartItem":
                return exportChildrenAsGroup(element, converted);

            // photo collages are typically used within
            // tray containers and should only be exported as
            // a group when inside of an actual PhotoCollage
            case "PhotoCollageItem":
                if (isInsideElementType(element, "PhotoCollage")) {
                    return exportSelfAsGroup(element, converted);
                }

            // if a layer provides its own conversion process
            default:
                if (element.convertToAuthoring && !element.isExporting) {
                    return exportCustomAsGroup(element, converted);
                }
        }
    }

    switch (element.type) {
        case "Header":
        case "Footer":
            return exportLayoutComponent(element, converted);
        case "TableFrame":
            return exportTable(element, converted);
        case "ProductScreenshotImage":
            return exportContent(element, converted);
        case "OrgChartConnectors":
        case "GanttChartGridLines":
        case "UserTestimonialDividers":
        case "UserTestimonialAttributionDivider":
        case "GridComponent":
            return exportComplexSVGLines(element, converted);

        case "UserTestimonialItemDesignQuote":
            return exportUserTestimonialQuote(element, converted);

        case "TextListCheckBox":
        case "Icon":
            return exportContent(element, converted);

        case "GanttChartGridLabels":
            return exportSVGTextNodes(element, converted);

        case "BigNumberChangeInValueIcon":
        case "PictographIcons":
        case "PictorialChartItemIcons":
        case "WordCloudElement":
            return exportSVG(element, converted);

        // for the journey slide, it doesn't know how to
        // determine the stroke for the road, so just assign
        // a single stroke value
        // we might need to refactor the Journey slide to break
        // up the road graphic into mulitple SVGs
        case "JourneySvg":
            const journey = exportSVG(element, converted);
            journey.strokeWidth = 2;
            return journey;

        case "Dashboard":
            return exportDashboard(element, converted);
        case "Thermometer":
        case "Countdown":
        case "Timer":
            // case "RadialBarGraph":
            return exportElementComponent(element, converted);
    }

    let shouldExportDecoration = true;

    if (element.findClosestOfType(FramedMediaElement)) {
        shouldExportDecoration = false;
    }

    if (element.decoration && element.decoration.hasBackground && shouldExportDecoration) {
        exportDecoration(element, converted, "background");
    }

    if (element instanceof TextElement) {
        exportText(element, converted);
    } else if (element instanceof ContentElement) {
        exportContent(element, converted);
    } else if (element instanceof Icon) {
        return exportContent(element, converted);
    } else if (element instanceof Video) {
        return exportVideo(element, converted);
    } else if (element instanceof ConnectorItem) {
        return exportConnectorItem(element, converted);
    } else if (element instanceof SVGElement) {
        switch (element.type) {
            case "SVGPathElement":
                exportSVG(element, converted);
                break;
            case "SVGPolygonElement":
            case "SVGPolylineElement":
                exportPath(element, converted);
                break;
            case "SVGRectElement":
                exportRect(element, converted);
                break;
            case "SVGCircleElement":
                exportCircle(element, converted);
                break;
            default:
                exportSVG(element, converted);
        }
        return;
    } else if (element instanceof StreetMap) {
        return exportStreetMap(element, converted);
    }

    await exportElementChildren(element, converted);

    if (!element.convertDecorationToAuthoringBeforeChildren && shouldExportDecoration) {
        exportForegroundDecoration(element, converted);
    }
}

async function exportElementChildren(element, converted) {
    let children;

    // check for a manual item return
    if (element.getElementsForAuthoringConversion) {
        children = element.getElementsForAuthoringConversion();
    } else {
        children = _.sortBy(element.elements, element => element.calculatedProps ? element.calculatedProps.layer : null);
    }

    if (element.convertDecorationToAuthoringBeforeChildren) {
        exportForegroundDecoration(element, converted);
    }

    if (children?.length) {
        for (const childElement of children) {
            if (childElement) {
                await convertElementToAuthoring(childElement, converted);
            }
        }
    }
}

function exportForegroundDecoration(element, converted) {
    if (element.decoration?.hasForeground) {
        exportDecoration(element, converted, "foreground");
    }
}

function convertDOMNodeBounds(domNode) {
    const canvasBounds = geom.Rect.FromBoundingClientRect(app.currentCanvas.el.getBoundingClientRect());

    const bounds = geom.Rect.FromBoundingClientRect(domNode.getBoundingClientRect())
        .offset(-canvasBounds.left, -canvasBounds.top)
        .multiply(1 / app.currentCanvas.canvasScale);

    return bounds;
}

function fitBoundsToPoints(bounds, points) {
    bounds = bounds.clone();

    let left = _.minBy(points, pt => pt.x).x;
    const width = _.maxBy(points, pt => pt.x).x - left;
    let top = _.minBy(points, pt => pt.y).y;
    const height = _.maxBy(points, pt => pt.y).y - top;

    left += bounds.x;
    top += bounds.y;

    // Normalize path points coordinates
    for (const point of points) {
        point.x += bounds.left - left;
        point.y += bounds.top - top;
    }

    bounds.left = left;
    bounds.top = top;
    bounds.width = width;
    bounds.height = height;

    const minSize = 10;
    // We don't want the shape to have width or height less than minSize
    // which is possible for vertical or horizontal lines
    if (bounds.width < minSize) {
        const diff = (minSize - bounds.width) / 2;
        bounds.left -= diff;
        points.forEach(point => point.x += diff);
        bounds.width = minSize;
    }
    if (bounds.height < minSize) {
        const diff = (minSize - bounds.height) / 2;
        bounds.top -= diff;
        points.forEach(point => point.y += diff);
        bounds.height = minSize;
    }

    return bounds;
}

function convertColor(color, opacity) {
    if (color && color != "none") {
        color = tinycolor(color);

        color.setAlpha(color.getAlpha() * (opacity || 1));
        return color.toRgbString();
    } else {
        return "none";
    }
}

function getStylesFromNode(node) {
    let fill, stroke, strokeWidth;

    while (node.getAttribute("fill") == null && node.getAttribute("style") == null) {
        if (node.parentElement) {
            node = node.parentElement;
        } else {
            break;
        }
    }

    if (node && node.getAttribute("fill")) {
        fill = convertColor(node.getAttribute("fill"), node.getAttribute("fill-opacity"));
        stroke = convertColor(node.getAttribute("stroke"), node.getAttribute("stroke-opacity"));
        strokeWidth = parseFloat(node.getAttribute("stroke-width"));
    } else if (node && node.getAttribute("style")) {
        let style = node.getAttribute("style");
        fill = convertColor(style.match(/fill: (.*?);/)?.[1], style.match(/fill-opacity: (.*?);/)?.[1]);
        stroke = convertColor(style.match(/stroke: (.*?);/)?.[1], style.match(/stroke-opacity: (.*?);/)?.[1]);
        strokeWidth = parseFloat(style.match(/stroke-width: (.*?);/)?.[1]);
    }

    return { fill, stroke, strokeWidth };
}

function exportUserTestimonialQuote(element, converted) {
    exportSVG(element, converted, {
        ignoreOffset: true,
        transformSVG: svg => {
            // remove the styling transforms
            const container = svg.querySelector("g");
            container.style.transform = "";
            container.style.position = "";
        }
    });
}

function exportText(element, converted) {
    // let bounds = convertDOMNodeBounds(element.DOMNode).inflate({ top: 0, bottom: 0 }).offset(0, -4);
    let bounds = convertDOMNodeBounds(element.DOMNode).inflate({ top: 20, bottom: 20 }).deflate({ left: element.styles.paddingLeft, right: element.styles.paddingRight });

    let lines = [];

    let textProps = {};
    textProps.fontColor = element.fontColor;
    textProps.boldFontColor = element.boldFontColor;

    for (let lineIndex = 0; lineIndex < element.textLayout.lines.length; lineIndex++) {
        let textRuns = [];
        let currentRun = { text: "", options: { break: false }, isFirstWord: true };
        let paragraphIndex = 0;

        let line = element.textLayout.lines[lineIndex];
        currentRun.isParagraphStart = line.isParagraphStart;

        for (let i = 0; i < line.words.length; i++) {
            let word = line.words[i];

            if (i == line.words.length - 1 && word.text == " " && element.paragraphStyle == "paragraph") {
                continue; // skip trailing spaces at the end of a line because we are manually breakiing lines
            }

            let str = word.formattedText;
            switch (element.styles.textTransform) {
                case "uppercase":
                    str = str.toUpperCase();
                    break;
                case "lowercase":
                    str = str.toLowerCase();
                    break;
                case "titlecase":
                    str = str.toTitleCase();
                    break;
            }

            let options = { break: false };

            if (word.style.includes("bold")) {
                options.color = textProps.boldFontColor;
                options.bold = true;

                // NOTE: bold seems to be separate from font weight in our current rendering system,
                //   so checking font weight doesn't make sense.
                //   Leaving this code commented in case this change introduces a regression.

                // if (element.font.styles.some(style => style.weight > element.baseFontWeight)) {
                //     options.bold = true;
                // }
            } else if (word.style.includes("italic")) {
                if (element.hasItalicFont) {
                    options.italic = true;
                }
            }

            if (word.color) {
                options.color = app.currentTheme.palette.getColor(word.color).toRgbString();
            }

            if (word.link) {
                let url = word.link;
                if (!url.startsWith("http://") && !url.startsWith("https://")) {
                    url = "https://" + url;
                }
                options.hyperlink = { url: url };
            }

            // remove any line breaks
            str = str.replace(/(\r\n|\n|\r)/gm, "");

            if (str == "") continue;

            // check if we need to create a new run because styles have changed
            let needsNewRun = isObjectDiff(options, currentRun.options) && !currentRun.isFirstWord;
            // for paragraphs we add a break within each line of the paragraph so we can match linebreaks
            if (element.paragraphStyle == "paragraph" && lineIndex > 0 && i == 0 && line.paragraphIndex == paragraphIndex) {
                // needsNewRun = true;
                str = "\n" + str;
            }
            // for bullet and numbered list, we only create a new run if this is a new paragraph
            if (line.paragraphIndex != undefined && line.paragraphIndex != paragraphIndex) {
                needsNewRun = true;
            }

            if (needsNewRun) {
                // save the current run
                if (currentRun) {
                    textRuns.push(currentRun);
                }

                // and create a new run
                currentRun = { text: str, options: { ...options } };
            } else {
                currentRun.text += str; // append text to current run
                currentRun.options = Object.assign(currentRun.options, { ...options });
                currentRun.isFirstWord = false;
            }

            paragraphIndex = line.paragraphIndex;
        }
        textRuns.push(currentRun);
        lines.push(textRuns);
    }

    // Retain the element's fontWeight if it differs from the current theme style's weight
    textProps.fontWeight = element.font.baseStyle.weight != element.baseFontWeight ? element.baseFontWeight : null;
    textProps.fontSize = element.calculatedProps.fontSize;
    textProps.lineHeight = element.calculatedProps.textLayout.lines[0].height / element.calculatedProps.fontSize;
    textProps.charSpacing = parseFloat((element.styles.letterSpacing * element.calculatedProps.fontSize).toFixed(2));

    let blocks = [];

    if (element.paragraphStyle == ParagraphStyle.PARAGRAPH) {
        textProps.textStyle = TextStyleType.BODY;
        switch (element.styles.fontStyle) {
            case "header":
            case "display":
                textProps.textStyle = TextStyleType.HEADING;
                break;
            default:
                textProps.textStyle = element.styles.fontStyle;
        }

        blocks.push(createTextBlock(textProps, lines));
    } else {
        textProps.textStyle = element.paragraphStyle == ParagraphStyle.BULLET_LIST ? TextStyleType.BULLET_LIST : TextStyleType.NUMBERED_LIST;

        let lineIndex = 0;
        let paragraphLines = [];
        while (lineIndex < lines.length) {
            if (lines[lineIndex][0].isParagraphStart) {
                if (paragraphLines.length > 0) {
                    blocks.push(createTextBlock(textProps, paragraphLines));
                    paragraphLines = [];
                }
            }
            paragraphLines.push(lines[lineIndex]);
            lineIndex++;
        }
        if (paragraphLines.length > 0) {
            blocks.push(createTextBlock(textProps, paragraphLines));
        }
    }

    let model = {
        type: AuthoringElementType.SHAPE,
        shape: AuthoringShapeType.RECT,
        ...bounds.toXYObject(),
        fill: "none",
        stroke: "none",
        strokeWidth: 0,
        textAlign: element.calculatedProps.textAlign,
        // verticalAlign: element.calculatedProps.verticalAlign,
        verticalAlign: VerticalAlignType.MIDDLE,
        textInset: 0,
        fitToText: false,
        blocks,
        blockGap: 15
    };

    converted.push(model);
}

function createTextBlock(textProps, lines) {
    const htmlLines = [];
    for (const textRuns of lines) {
        let lineHtml = "";
        for (const run of textRuns) {
            let htmlRun = run.text;
            if (run.options.italic) {
                htmlRun = `<i>${htmlRun}</i>`;
            }
            if (run.options.bold) {
                htmlRun = `<b>${htmlRun}</b>`;
            }
            if (run.options.color) {
                htmlRun = `<font style="color: ${tinycolor(run.options.color).toHexString()}">${htmlRun}</font>`;
            }
            if (run.options.hyperlink) {
                htmlRun = `<a href="${run.options.hyperlink.url}">${htmlRun}</a>`;
            }
            lineHtml += htmlRun;
        }
        htmlLines.push(lineHtml);
    }

    let html = htmlLines.join("<br>");
    if (html.endsWith("<br>")) {
        html += "<br>";
    }

    return {
        id: _.uniqueId("block"),
        type: AuthoringBlockType.TEXT,
        textStyle: textProps.textStyle,
        html,
        fontSize: textProps.fontSize,
        fontColor: textProps.fontColor,
        fontWeight: textProps.fontWeight,
        lineHeight: textProps.lineHeight,
        letterSpacing: textProps.letterSpacing
    };
}

function exportLayoutComponent(element, converted) {
    let model = {
        ...convertDOMNodeBounds(element.DOMNode).toXYObject(),
        ...element.model
    };

    switch (element.type) {
        case "Header":
            model.type = AuthoringElementType.HEADER;
            model.headerPosition = element.position;
            if (element.model.userFontScale) {
                model.userFontScale = {};
                for (let key of Object.keys(element.model.userFontScale)) {
                    model.userFontScale[key.replace(/Header/, "AuthoringCanvas/AuthoringElementContainer/Header")] = element.model.userFontScale[key];
                }
            }
            break;
        case "Footer":
            model.type = AuthoringElementType.FOOTER;
            break;
    }

    converted.push(model);
}

// checks if the converted list already contains an ID
// meaning that element has already been converted - helps
// to prevent duplicate conversions
function hasAlreadyConverted(id, converted) {
    return !!converted.find(el =>
        el.id === id ||
        el.model?.id === id ||
        el.element?.id === id
    );
}

// rebases targets and sources to a new container
function rebasePaths(authoringContainerId, model) {
    // is a collection of items
    if (isArray(model)) {
        return model.forEach(value => rebasePaths(authoringContainerId, value));
    }

    // Since the converted chart will have a new path (unique id) we have to update all
    // references to it from its annotations
    const keysContainingElementId = ["elementId", "source", "target"];
    Object.entries(model).forEach(([key, value]) => {
        if (keysContainingElementId.includes(key) && typeof value === "string" && value.startsWith("/primary/")) {
            model[key] = value.replace(/^\/primary\//, `/primary/${authoringContainerId}/element/`);
        } else if (value && typeof value === "object") {
            rebasePaths(authoringContainerId, value);
        }
    });
}

// extract opacity and verifies the value
function exportOpacity(opacity) {
    const converted = opacity * 100;
    return isNaN(converted) ? 100 : converted;
}

function exportDashboard(element, converted) {
    const convertedDashboardModel = _.cloneDeep(element.model);
    convertedDashboardModel.items
        .forEach(dashboardItem => {
            const authoringContainerId = uuid();

            // charts have annotations that need to be converted
            if (dashboardItem.elementType === "chart") {
                const chartAnnotationsModel = dashboardItem.chart.chartAnnotations;
                if (chartAnnotationsModel) {
                    rebasePaths(authoringContainerId, chartAnnotationsModel);
                }
            }

            // get the bounds of the child element so it can be
            // used to create the new mini-dashboard
            const childElement = element.elements[dashboardItem.id];
            const bounds = convertDOMNodeBounds(childElement.DOMNode).toXYObject();

            // save each as a separate dashboard
            converted.push({
                id: authoringContainerId,
                type: AuthoringElementType.COMPONENT,
                componentType: element.type,
                ...bounds,
                element: {
                    ...convertedDashboardModel,
                    id: authoringContainerId,
                    items: [dashboardItem]
                },
            });
        });
}

function exportElementComponent(element, converted) {
    const clone = {
        id: uuid(),
        type: AuthoringElementType.COMPONENT,
        componentType: element.type,
        ...convertDOMNodeBounds(element.DOMNode).toXYObject(),
        element: { ...element.model }
    };

    // add arrays that require conversions to
    // include with rebase paths
    [
        // Thermometer
        clone.element.annotations?.connections?.items

    ].forEach(collection => {
        if (collection) {
            rebasePaths(clone.id, collection);
        }
    });

    converted.push(clone);
}

function exportTable(element, converted) {
    let bounds = convertDOMNodeBounds(element.table.DOMNode);
    element.model.tableHeight = 1;
    converted.push({
        type: AuthoringElementType.COMPONENT,
        componentType: element.type,
        ...bounds.toXYObject(),
        element: { ...element.model }
    });
}

function exportWordCloud(element, converted) {
    let bounds = convertDOMNodeBounds(element.DOMNode);

    element.DOMNode.querySelectorAll("path").forEach(pathNode => {
        let style = pathNode.getAttribute("style");
        let transformX = parseFloat(style.match(/translateX\((.*?)\)/)[1]) / element.canvas.getScale();
        let transformY = parseFloat(style.match(/translateY\((.*?)\)/)[1]) / element.canvas.getScale();

        let pathBounds = convertDOMNodeBounds(pathNode).offset(bounds.left, bounds.top);
        pathBounds.left = transformX;
        pathBounds.top = transformY;

        converted.push({
            type: AuthoringElementType.SHAPE,
            shape: AuthoringShapeType.RAW_SVG,
            svgHTML: pathNode.outerHTML,
            ...getStylesFromNode(pathNode),
            ...pathBounds.toXYObject()
        });
    });
}

function exportContent(element, converted) {
    let isFramed = element.parentElement instanceof FramedMediaElement && (element.parentElement.frameType != "none" && element.parentElement.frameType != "unframedRect");

    let bounds;

    if (isFramed) {
        bounds = convertDOMNodeBounds(element.parentElement.DOMNode);
    } else {
        bounds = convertDOMNodeBounds(element.DOMNode).deflate(element.styles.padding);
    }

    let elementModel = { ...element.model };

    if (isFramed) {
        elementModel.frameType = element.parentElement.frameType; // update the model frameType to reflect the frameType getter which may differ from the model.frameType
        if (!elementModel.color || elementModel.color == "auto") {
            // we need to use the theme-driven colors here because there is no user-defined color
            if (element.decoration.styles.resolved_fillColor.name == "none") {
                elementModel.color = element.decoration.styles.resolved_strokeColor.name;
            } else {
                elementModel.color = element.decoration.styles.resolved_fillColor.name;
            }
        }
    }

    // elementModel.frameType = elementModel.frame;
    // delete elementModel.frame;

    // if an icon is directly passed in then we need
    // to determine which properties to use to convert
    // it into a content element
    if (element instanceof Icon) {
        // this content has already been converted by a previous attempt
        if (hasAlreadyConverted(element.model.id, converted)) {
            return;
        }

        let iconColor = element.styles.resolved_fillColor.name;
        // classic slide icons only allow background dark and light - so this converts primary to background
        if (iconColor == "primary_dark") {
            iconColor = "background_dark";
        }
        if (iconColor == "primary_light") {
            iconColor = "background_light";
        }

        elementModel = {
            id: element.model.id || uuid(),
            color: iconColor,
            content_type: AssetType.ICON,

            // TODO: are there more places where the "Icon" might not
            // be this property?
            content_value: element.iconId
        };

        bounds = bounds.scale(element.calculatedProps.scale);
    } else if (element.assetType == AssetType.ICON) {
        if (!isFramed) {
            elementModel.color = element.assetElement.styles.resolved_fillColor.name;
            bounds = bounds.scale(element.assetElement.calculatedProps.scale);
        }
    }
    if (!isFramed && element.assetType == AssetType.IMAGE) {
        if (element.decoration?.styles?.shape == "circle" || element.assetElement.calculatedProps.clipPath?.startsWith("circle")) {
            if (element.assetElement.calculatedProps.clipPath) {
                let r = parseFloat(element.assetElement.calculatedProps.clipPath.split(" ")[2]);
                bounds = bounds.deflate((bounds.width - r * 2) / 2);
            }
            elementModel.frameType = "circle";
        }
    }

    converted.push({
        type: AuthoringElementType.CONTENT,
        ...bounds.toXYObject(),
        element: elementModel,
        opacity: exportOpacity(element.styles.opacity)
    });
}

function exportSVGTextNodes(element, converted) {
    // create the default text props
    const { styles } = element;
    const textProps = {
        fontColor: styles.resolved_fontColor.toRgbString(),
        fontId: styles.fontId,
        fontScaling: styles.fontScaling,
        fontSize: styles.fontSize,
        fontStyle: styles.fontStyle,
        fontWeight: styles.fontWeight,
        lineHeight: styles.lineHeight,
    };

    // generate a text object for each node
    const textNodes = element.DOMNode.querySelectorAll("text");
    for (const textNode of textNodes) {
        const content = textNode.textContent;
        const bounds = convertDOMNodeBounds(textNode);
        const run = [{ text: content, options: { bold: true } }];
        const block = createTextBlock(textProps, [run]);

        // save the text node
        converted.push({
            type: AuthoringElementType.SHAPE,
            shape: AuthoringShapeType.RECT,
            ...bounds.toXYObject(),
            fill: "none",
            stroke: "none",
            strokeWidth: 0,
            textAlign: element.calculatedProps.textAlign,
            verticalAlign: VerticalAlignType.MIDDLE,
            textInset: 0,
            fitToText: false,
            blocks: [block],
            blockGap: 15
        });
    }
}

function exportVideo(element, converted) {
    const bounds = convertDOMNodeBounds(element.DOMNode).deflate(element.styles.padding);
    const elementModel = {
        ...element.model,
        fullBleed: false,
    };

    const model = {
        type: AuthoringElementType.VIDEO,
        x: bounds.x,
        y: bounds.y,
        width: bounds.width,
        height: bounds.height,
        element: elementModel,
    };

    converted.push(model);
}

function exportStreetMap(element, converted) {
    converted.push({
        type: AuthoringElementType.CONTENT,
        ...convertDOMNodeBounds(element.DOMNode).toXYObject(),
        element: {
            content_type: "image",
            content_value: element.model.assetId
        }
    });
}

async function exportConnectorItem(connectorItemElement, converted) {
    const connectorBounds = convertDOMNodeBounds(connectorItemElement.DOMNode);

    // Stripping out adjustments
    const points = connectorItemElement.fullConnectorPath.points.map(({ x, y }) => ({ x, y }));
    // Adjusting bounds
    {
        let left = _.minBy(points, pt => pt.x).x;
        let width = _.maxBy(points, pt => pt.x).x - left;
        let top = _.minBy(points, pt => pt.y).y;
        let height = _.maxBy(points, pt => pt.y).y - top;

        left += connectorBounds.left;
        top += connectorBounds.top;
        width = Math.max(width, 1);
        height = Math.max(height, 1);

        // Normalize path points coordinates
        for (const point of points) {
            point.x += connectorBounds.left - left;
            point.y += connectorBounds.top - top;
        }

        connectorBounds.left = left;
        connectorBounds.top = top;
        connectorBounds.width = width;
        connectorBounds.height = height;
    }

    const connectorPathElement = connectorItemElement.path;
    let strokeStyle = "solid";
    if (connectorItemElement.model.lineStyle === "dotted") {
        strokeStyle = "dotted";
    } else if (connectorItemElement.model.lineStyle === "dashed" || connectorItemElement.model.lineStyle === "animate_dash") {
        strokeStyle = "dashed";
    }

    let strokeWidth = connectorItemElement.model.lineWeight || connectorItemElement.calculatedProps.strokeWidth;
    if (strokeWidth == "bold") {
        strokeWidth = 10;
    }

    const model = {
        ...connectorBounds.toXYObject(),
        type: AuthoringElementType.PATH,
        points,
        fill: "none",
        stroke: connectorItemElement.getConnectorColor(),
        strokeWidth,
        strokeStyle,
        startDecoration: connectorItemElement.model.startDecoration || "none",
        endDecoration: connectorItemElement.model.endDecoration || "none",
        arrowHeads: connectorItemElement._loadedStyles.arrowHeads,
        opacity: connectorItemElement.styles.connectorOpacity * 100
    };

    converted.push(model);

    // convert the labels, if any
    return await convertElementToAuthoring(connectorItemElement.labels, converted);
}

function exportPath(element, converted) {
    let bounds = convertDOMNodeBounds(element.DOMNode);
    let points = element.calculatedProps.path.map(pt => ({ x: pt[0], y: pt[1] }));

    bounds = fitBoundsToPoints(bounds, points);

    let fillColor = convertColor(element.styles.resolved_fillColor?.toRgbString() ?? "none", element.styles.fillOpacity || 1);
    let strokeColor = convertColor(element.styles.resolved_strokeColor?.toRgbString() ?? "none", element.styles.strokeOpacity || 1);

    // check for dashes
    let strokeStyle;
    if (element.styles.strokeDash > 0) {
        // TODO: using 2 since it visually looks correct,
        // but is there a better way to identify which kind to use?
        strokeStyle = element.styles.strokeDash <= 2 ? "dotted"
            : "dashed";
    }

    converted.push({
        type: AuthoringElementType.PATH,
        ...bounds.toXYObject(),
        points,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: element.styles.strokeWidth,
        opacity: exportOpacity(element.styles.opacity),
        strokeStyle
    });
}

function exportComplexSVGLines(element, converted) {
    let bounds = convertDOMNodeBounds(element.DOMNode);

    element.DOMNode.querySelectorAll("polyline, line").forEach(node => {
        let points;

        if (node.getAttribute("points")) {
            points = node.getAttribute("points").split(" ").filter(o => o !== "").map(o => o.split(",").map(o => parseFloat(o)));
            points = points.map(arr => ({ x: arr[0], y: arr[1] }));
        } else if (node.getAttribute("x1")) {
            points = [
                {
                    x: parseFloat(node.getAttribute("x1")),
                    y: parseFloat(node.getAttribute("y1"))
                }, {
                    x: parseFloat(node.getAttribute("x2")),
                    y: parseFloat(node.getAttribute("y2"))
                }
            ];
        }

        let pathBounds = fitBoundsToPoints(bounds, points);
        let styles = getStylesFromNode(node);

        converted.push({
            type: AuthoringElementType.PATH,
            ...pathBounds.toXYObject(),
            points,
            ...styles
        });
    });
}

function exportSVG(element, converted, { node, ignoreOffset, transformSVG } = { }) {
    const svgNode = node || element.DOMNode.firstElementChild;
    let svgBounds = convertDOMNodeBounds(svgNode);

    let {
        preserveFill,
        preserveStroke,
    } = element.options;

    // check for the offset required
    let offsetX = 0;
    let offsetY = 0;
    if (!ignoreOffset) {
        const elementBounds = convertDOMNodeBounds(element.DOMNode);
        offsetX = svgBounds.left - elementBounds.left;
        offsetY = svgBounds.top - elementBounds.top;
    }

    // ensure minimum bounds to make it easier
    // to work with small shapes such as lines
    const MINIMUM_BOUNDS = 5;
    const growX = Math.abs(Math.min(0, svgBounds.width - MINIMUM_BOUNDS)) * 0.5;
    const growY = Math.abs(Math.min(0, svgBounds.height - MINIMUM_BOUNDS)) * 0.5;
    svgBounds = svgBounds.inflate({ top: growY, bottom: growY, left: growX, right: growX });
    offsetX -= growX * 0.5;
    offsetY -= growY * 0.5;

    const nodeStyles = getStylesFromNode(svgNode);
    const normalizedSvgNode = svgNode.cloneNode(true);

    // check for a custom function to normalize the SVG contents
    if (transformSVG) {
        transformSVG(normalizedSvgNode, nodeStyles);
    }

    // attempts to merge styles found on child nodes to the main
    // style collection
    function mergeStyles(merge) {
        if (nodeStyles.fill === "none" && merge.fill && merge.fill !== "none") {
            nodeStyles.fill = merge.fill;
        }

        if (nodeStyles.stroke === "none" && merge.stroke && merge.stroke !== "none") {
            nodeStyles.stroke = merge.stroke;
        }

        if (isNaN(nodeStyles.strokeWidth) && !isNaN(merge.strokeWidth)) {
            nodeStyles.strokeWidth = merge.strokeWidth;
        }
    }

    // find all nodes that have assigned fills and strokes
    // querySelectorAll may not capture some elements as root, so move
    // everything inside of a container node first
    const container = document.createElement("div");
    container.appendChild(normalizedSvgNode);
    const targets = container.querySelectorAll("[fill], [fill-opacity], [stroke], [stroke-width], [stroke-dasharray], [style]");

    // perform the conversion for each
    for (const target of targets) {
        // check each child node for styles and merge them into
        // the root styles
        const localStyles = getStylesFromNode(target);
        mergeStyles(localStyles);

        if (!preserveFill) {
            // had a fill assigned as a style
            if (target.style.fill && target.style.fill !== "none") {
                localStyles.fill = target.style.fill;
                target.style.removeProperty("fill");
            } else if (target.hasAttribute("fill")) {
                // has a fill assigned as an attribute
                localStyles.fill = target.getAttribute("fill");
                target.setAttribute("fill", "@fill");
            } else {
                // appears to not have a fill
                localStyles.fill = `rgba(0,0,0,0)`;
            }

            // always tell to use something?
            target.setAttribute("fill", "@fill");
        }

        if (!preserveStroke) {
            // had a fill assigned as a style
            if (target.style.stroke && target.style.stroke !== "none") {
                localStyles.stroke = target.style.stroke;
                target.style.removeProperty("stroke");
            } else if (target.hasAttribute("stroke")) {
                // has a stroke assigned as an attribute
                localStyles.stroke = target.getAttribute("stroke");
                target.setAttribute("stroke", "@stroke");
            } else {
                // appears to not have a stroke
                localStyles.stroke = `rgba(0,0,0,0)`;
            }

            // always tell to use something?
            target.setAttribute("stroke", "@stroke");
        }
    }

    // just in case
    if (isNaN(nodeStyles.strokeWidth) || !_.isNumber(nodeStyles.strokeWidth)) {
        nodeStyles.strokeWidth = 0;
    }

    // create the final svg markup
    let svgHTML;
    svgHTML = new XMLSerializer().serializeToString(normalizedSvgNode);
    svgHTML = `<g transform="translate(${-offsetX} ${-offsetY})">${svgHTML}</g>`;

    const model = {
        type: AuthoringElementType.SHAPE,
        shape: AuthoringShapeType.RAW_SVG,
        svgHTML,
        preserveAspectRatio: true,
        canChangeFill: true,
        canChangeStroke: true,
        viewBox: svgBounds.zeroOffset().toXYObject(),
        ...nodeStyles,
        ...svgBounds.toXYObject()
    };

    converted.push(model);
    return model;
}

function exportRect(element, converted) {
    const svgNode = element.DOMNode.firstElementChild;

    const model = {
        type: AuthoringElementType.SHAPE,
        shape: AuthoringShapeType.RECT,
        ...getStylesFromNode(svgNode),
        strokeStyle: "solid",
        opacity: exportOpacity(element.styles.opacity),
        ...convertDOMNodeBounds(svgNode).toXYObject()
    };

    converted.push(model);
}

function exportCircle(element, converted) {
    const svgNode = element.DOMNode.firstElementChild;

    const model = {
        type: AuthoringElementType.SHAPE,
        shape: AuthoringShapeType.ELLIPSE,
        ...getStylesFromNode(svgNode),
        strokeStyle: "solid",
        opacity: exportOpacity(element.styles.opacity),
        ...convertDOMNodeBounds(svgNode).toXYObject()
    };

    converted.push(model);
}

function exportDecoration(element, converted, position) {
    const decorationElement = element.decoration;
    const ref = position === "background" ? decorationElement.backgroundRef : decorationElement.foregroundRef;
    const domNode = ref.current;
    if (!domNode) {
        return;
    }

    const { type, shape, cornerRadius } = decorationElement.styles;
    const isSvg = type === "svg";

    const bounds = convertDOMNodeBounds(domNode);
    const model = {
        ...bounds.toXYObject()
    };

    const isRoundedRect = shape === DecorationType.RECT && cornerRadius;

    if (isSvg || shape === DecorationType.OCTAGON) {
        exportSVG(element, converted, { node: domNode });
    } else if (shape === DecorationType.RECT || shape === DecorationType.CIRCLE) {
        model.type = AuthoringElementType.SHAPE;

        switch (shape) {
            case DecorationType.RECT:
                model.shape = AuthoringShapeType.RECT;
                break;
            case DecorationType.CIRCLE:
                model.shape = AuthoringShapeType.ELLIPSE;
                break;
        }

        if (position == "background" && domNode.style.backgroundColor && domNode.style.backgroundColor !== "none") {
            model.fill = domNode.style.backgroundColor;
            // if (element.parentElement?.styles?.shadow) {
            //     // special case for shadow on PhotoCollage
            //     let [color, x, y, blur] = element.parentElement.styles.replace(/, /g, ",").split(" ");
            //     model.shadow = { color, x: parseInt(x), y: parseInt(y), blur: parseInt(blur) };
            // }
            if (domNode.style.boxShadow) {
                let [color, x, y, blur] = domNode.style.boxShadow.replace(/, /g, ",").split(" ");
                model.shadow = { color, x: parseInt(x), y: parseInt(y), blur: parseInt(blur) / 2 };
            }
        } else {
            model.fill = "none";
        }

        if (position == "foreground" && domNode.style.borderColor && domNode.style.borderColor !== "none" && parseFloat(domNode.style.borderWidth) > 0) {
            model.stroke = domNode.style.borderColor;
            model.strokeWidth = parseFloat(domNode.style.borderWidth);
        } else {
            model.stroke = "none";
        }

        if (cornerRadius && shape === DecorationType.RECT) {
            model.adj1 = cornerRadius;
        }

        if (model.fill == "none" && model.stroke == "none") {
            return;
        }

        converted.push(model);
    }
}
